import { readContext, searchFor, type LedgerContext } from '../ledger/context';

/**
 * The shell's screens, their addresses, and the menu that reaches them (C4a shell fix).
 *
 * Until this, the signed-in shell had no navigation at all: `App.tsx` stacked the ledger, the
 * recurring entries and the reports in one column on one path, and the only navigation-shaped thing
 * in the client was the ledger's own query string. Every purpose was reachable only by scrolling.
 *
 * The shape here is the operator's, corrected by him on 2026-10-18 after walking it on the live
 * site. His first answer that day — given on the researcher's plan for this fix, approved on all
 * four points — was a **left rail on desktop, a wrapping row of the same items below 900 px**, with
 * **the session block in the menu's foot**. He then used it and replaced that shape:
 *
 *   *"Put the menu as a menu bar on the top (just 1 row), Sign out also goes to the top menu bar.
 *   Your session goes to the bottom. Use icons as well."*
 *
 * So, of the four points, the first and the fourth stand and the middle two are history:
 *
 *   1. **One path per purpose**, with the ledger left at `/`. The ledger does not move, so every
 *      deep link the C4a walkthrough pack hands out — `/?account=…&month=…`, with and without
 *      `&canceled=true` — still opens what it names, and so do the browser suite's 41 deep links.
 *   2. **A single-row menu bar across the top, at every width** — no left rail, on desktop or
 *      anywhere else — with `aria-current="page"` on the screen you are on and an icon beside each
 *      label. The bar's own shape and its measurements are in `Menu.tsx` and `styles.css`.
 *   3. **Sign out in the bar, and the session block at the screen's foot**, so the owner id and the
 *      session controls are on every screen. `App.tsx`'s shell renders both, once.
 *   4. **The categories panel on the Accounts screen**, with the rest of the setup surfaces.
 *
 * Two rules hold the address together, and both are pure functions here so they are asserted in the
 * fast suite rather than inferred from a browser journey:
 *
 *   - **An unknown path is replaced by its default** (`/`, the ledger) instead of being shown as an
 *     error — the rule `context.ts` already applies to a malformed parameter. `screenForPath`
 *     answers `null` for such a path and `readLocation` falls back, so the address the shell writes
 *     is the ledger's own canonical one.
 *   - **The ledger's query string belongs to the ledger's address.** `urlForLocation` writes the
 *     query on `/` and nowhere else, so `/reports` never claims a month it does not have. That is
 *     why `replayedLocation` keeps the context already held when a history move lands on another
 *     screen: the address says nothing about the ledger's filters there, and forgetting them would
 *     lose the view the reader returns to the moment they pick "Ledger" again.
 *
 * Nothing here touches `window` or `history` — the shell does that, in one place, which is what
 * keeps two components from ever writing the address.
 */

export type Screen = 'ledger' | 'accounts' | 'recurring' | 'reports';

/** The screen an address that names none is replaced by. */
export const DEFAULT_SCREEN: Screen = 'ledger';

/** Every screen's path. The ledger's is `/`, unchanged, and that is the point. */
const SCREEN_PATHS: Readonly<Record<Screen, string>> = {
  ledger: '/',
  accounts: '/accounts',
  recurring: '/recurring',
  reports: '/reports',
};

/** The address a screen lives at. */
export function pathFor(screen: Screen): string {
  return SCREEN_PATHS[screen];
}

export interface MenuItem {
  readonly screen: Screen;
  readonly label: string;
}

/**
 * The menu's items, in the operator's order.
 *
 * P9's *Imports and exports* becomes a fifth entry here — `{ screen: 'import', label: 'Import &
 * export' }`, with `/import` in `SCREEN_PATHS` above and its screen in `App` — when that phase
 * ships. It is deliberately **absent** until then rather than present and disabled: a control that
 * opens a screen the app does not have is worse than no control.
 *
 * The labels are short enough that the bar stays one row at 360 px (the widest, "Recurring", is nine
 * characters), and a fifth entry's own width is the thing to check against the measurement in
 * `styles.css` when it arrives, not a reason to let the row wrap.
 */
export const MENU_ITEMS: readonly MenuItem[] = [
  { screen: 'ledger', label: 'Ledger' },
  { screen: 'accounts', label: 'Accounts' },
  { screen: 'recurring', label: 'Recurring' },
  { screen: 'reports', label: 'Reports' },
];

/**
 * The screen a path names, or `null` when it names none.
 *
 * A trailing slash is the only normalisation: `/accounts/` is the Accounts screen, not an unknown
 * path. Anything else — `/nope`, `/ledger/2026/statement`, `/accounts/extra` — names no screen, and
 * the caller replaces the address rather than inventing a screen for it.
 */
export function screenForPath(pathname: string): Screen | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const found = (Object.keys(SCREEN_PATHS) as Screen[]).find((screen) => SCREEN_PATHS[screen] === path);
  return found ?? null;
}

/** An address read into what the shell renders: which screen, and the ledger's own context. */
export interface Location {
  readonly screen: Screen;
  readonly context: LedgerContext;
}

/**
 * Read an address into a location.
 *
 * `fallbackMonth` is the business month, as `context.ts` defines it, so an unaddressed visit opens
 * on the business "now" rather than on the browser's own month.
 */
export function readLocation(pathname: string, search: string, fallbackMonth: string): Location {
  return {
    screen: screenForPath(pathname) ?? DEFAULT_SCREEN,
    context: readContext(search, fallbackMonth),
  };
}

/**
 * The address for a location: the ledger's own query on the ledger's own path, and a bare path
 * everywhere else. `/reports?month=…` would claim a month that screen does not have.
 */
export function urlForLocation(location: Location): string {
  return location.screen === 'ledger'
    ? `/${searchFor(location.context)}`
    : pathFor(location.screen);
}

/**
 * The location after a back or forward move.
 *
 * A history entry for the ledger carries the ledger's context, so the address wins. An entry for
 * any other screen carries none, so the context already held is kept — see the module note.
 */
export function replayedLocation(held: Location, address: Location): Location {
  return address.screen === 'ledger' ? address : { screen: address.screen, context: held.context };
}
